import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Home, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'colaborador') {
        navigate('/colaborador');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1648052491345-9fae6eb54aae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBoeWJyaWQlMjB3b3JrfGVufDB8fHx8MTc3NTMyMTIwNnww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-[#002FA7]/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <img 
            src="/assets/logo-am-office.png" 
            alt="AM Office" 
            className="w-72 h-auto mb-10"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Gestión Inteligente de Asistencia
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Optimiza la distribución de asistencia presencial en tu oficina híbrida con equidad y transparencia.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <Card className="w-full max-w-md border border-slate-200 shadow-sm">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center mb-4">
              <img 
                src="/assets/logo-am-office.png" 
                alt="AM Office" 
                className="h-10 w-auto"
              />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Iniciar Sesión
            </CardTitle>
            <CardDescription>
              Ingresa tus credenciales para acceder al sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Correo Electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
